import { CommonModule } from "@angular/common";
import { APP_INITIALIZER, LOCALE_ID, ErrorHandler, NgModule } from "@angular/core";
import { Router } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule } from '@angular/common/http';
import { BrowserModule, provideClientHydration } from "@angular/platform-browser";
import * as Sentry from "@sentry/angular";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatIconModule } from "@angular/material/icon";
import { registerLocaleData } from "@angular/common";
import localePt from "@angular/common/locales/pt";
import { SweetAlert2Module } from "@sweetalert2/ngx-sweetalert2";
import { defineLocale } from "ngx-bootstrap/chronos";
import { BsDatepickerModule, BsLocaleService } from "ngx-bootstrap/datepicker";
import { ptBrLocale } from "ngx-bootstrap/locale";
import { PaginationModule } from "ngx-bootstrap/pagination";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { TypeaheadModule } from "ngx-bootstrap/typeahead";
import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { ToastrModule } from "ngx-toastr";
import { AppComponent } from "./app.component";
import { AppRoutingModule, routingComponents } from "./app.routes";
import { CartComponent } from "./components/cart/cart.component";
import { CreditCardComponent } from "./components/credit-card/credit-card.component";
import { SelecaoClientesComponent } from "./components/selecao-clientes/selecao-clientes.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HeaderComponent } from "./components/header/header.component";
import { NewsLetterComponent } from "./components/news-letter/news-letter.component";
import { NovoClienteComponent } from "./components/novo-cliente/novo-cliente.component";
import { SimpleHtmlModule } from "./components/simple-html/simple-html.module";
import { SliderImagemComponent } from "./components/slider-imagem/slider-imagem.component";
import { SliderPrincipalComponent } from "./components/slider-principal/slider-principal.component";
import { SliderSuperiorComponent } from "./components/slider-superior/slider-superior.component";
import { DirectiveModule } from "./directive/directives.module";
import { CatalogModule } from "./modules/catalog/catalog.module";
import { MaterialMarketingModule } from "./pages/material-marketing/material-marketing.module";
import { PageNotfoundComponent } from "./pages/page-notfound/page-notfound.component";
import { DateBRAPipe } from "./pipes/dateBRA.pipe";
import { FilterByKeyPipe } from "./pipes/filterByKey.pipe";
import { InitProvider } from "./core/services/init-provider";
import { LoginGuard } from "./core/services/loginguard";
import { AppConfigService } from "./modules/marketing/services/app-config.service";
import { ProductModule } from "./modules/product/product.module";
import { OrderModule } from "./modules/order/order.module";
import { TreeviewLinkDynamicComponent } from "./components/treeview-link-dynamic/treeview-link-dynamic.component";
import { TreeviewLinkComponent } from "./components/treeview-link/treeview-link.component";
import { TreeviewLinkDynamicHorizontalComponent } from "./components/treeview-link-dynamic-horizontal/treeview-link-dynamic-horizontal.component";
import { TreeviewLinkHorizontalComponent } from "./components/treeview-link-horizontal/treeview-link-horizontal.component";
import { TreeviewLinkHorizontalOpt2Component } from "./components/treeview-link-horizontal-opt2/treeview-link-horizontal-opt2.component";
import { TreeviewLinkHorizontalOpt3Component } from "./components/treeview-link-horizontal-opt3/treeview-link-horizontal-opt3.component";
import { CustomSelectComponent } from "./modules/catalog/components/custom-select/cusom-select.component";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
// import { DatepickerModule } from "ngx-bootstrap/datepicker";
import { PageVendasComponent } from "./pages/page-vendas/page-vendas.component";
import { NovoRepresentanteComponent } from "./components/novo-representante/novo-representante.component";
import { PageRelatoriosComponent } from "./pages/page-relatorios/page-relatorios.component";
import { CurrencyDirective } from "./directive/currency.directive";
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { NestedTableComponent } from "./components/nested-table/nested-table.component";
import { PageAtivacaoComponent } from "./pages/page-ativacao/ativacao.component";
import { CookieBannerComponent } from "./components/cookie-banner/cookie-banner.component";
import { PagePedidosWrapperComponent } from "./pages/page-pedidos-wrapper/page-pedidos-wrapper.component";
import { PagePedidosComponent } from "./pages/page-pedidos/page-pedidos.component";
import { ListaPedidoB2cComponent } from "./pages/page-pedidos-b2c/lista-pedido-b2c.component";
import { PageMeusDadosWrapperComponent } from "./pages/page-meus-dados-wrapper/page-meus-dados-wrapper.component";

const customLocale = {
  ...ptBrLocale,
  weekdays: [$localize`Domingo`,$localize`Segunda-feira`,$localize`Terça-feira`,$localize`Quarta-feira`,$localize`Quinta-feira`,$localize`Sexta-feira`,$localize`Sábado`],
  weekdaysShort: [$localize`Dom`,$localize`Seg`,$localize`Ter`,$localize`Qua`,$localize`Qui`,$localize`Sex`,$localize`Sáb`],
  months: [
    $localize`Janeiro`,$localize`Fevereiro`,$localize`Março`,$localize`Abril`,$localize`Maio`,$localize`Junho`,
    $localize`Julho`,$localize`Agosto`,$localize`Setembro`,$localize`Outubro`,$localize`Novembro`,$localize`Dezembro`
  ],
  monthsShort: [$localize`Jan`,$localize`Fev`,$localize`Mar`,$localize`Abr`,$localize`Mai`,$localize`Jun`,$localize`Jul`,$localize`Ago`,$localize`Set`,$localize`Out`,$localize`Nov`,$localize`Dez`],
};

/*counter*/
defineLocale("pt-custom", customLocale);
registerLocaleData(localePt, "pt-BR");

export function filtroProviderFactory(provider: InitProvider) {
  return () => provider.load();
}

@NgModule({
  declarations: [
    AppComponent,
    routingComponents,
    TreeviewLinkHorizontalOpt2Component,
    SelecaoClientesComponent,
    TreeviewLinkHorizontalOpt3Component,
    PageAtivacaoComponent,
    NestedTableComponent,
    // TypeaheadComponent,
    PagePedidosWrapperComponent,
    PageMeusDadosWrapperComponent,
    PagePedidosComponent,
    ListaPedidoB2cComponent,
    SliderPrincipalComponent,
    SliderSuperiorComponent,
    CustomSelectComponent,
    PageVendasComponent,
    PageRelatoriosComponent,
    HeaderComponent,
    FooterComponent,
    CookieBannerComponent,
    PageNotfoundComponent,
    SliderImagemComponent,
    FilterByKeyPipe,
    DateBRAPipe,
    NovoClienteComponent,
    NewsLetterComponent,
    CreditCardComponent,
    TreeviewLinkComponent,
    TreeviewLinkDynamicComponent,
    TreeviewLinkDynamicHorizontalComponent,
    TreeviewLinkHorizontalComponent,
    NovoRepresentanteComponent,
    CartComponent,
    CurrencyDirective,
  ],
  imports: [
    MatSelectModule,
    MatDatepickerModule,
    ProductModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    CatalogModule,
    OrderModule,
    MaterialMarketingModule,
    HttpClientModule,
    NgxMaskDirective,
    NgxMaskPipe,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    SimpleHtmlModule,
    ReactiveFormsModule,
    BsDatepickerModule.forRoot(),
    // DatepickerModule.forRoot(),
    // NgxMaskModule.forRoot(),
    PaginationModule.forRoot(),
    SweetAlert2Module.forRoot(),
    TypeaheadModule.forRoot(),
    TooltipModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: "toast-bottom-right",
    }),
    DirectiveModule,
  ],
  providers: [
   {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    LoginGuard,
    InitProvider,
    AppConfigService,
    provideNgxMask(),
    {
      provide: APP_INITIALIZER,
      useFactory: filtroProviderFactory,
      deps: [InitProvider, Sentry.TraceService],
      multi: true,
    },
    { provide: LOCALE_ID, useValue: "pt-BR" },
    provideClientHydration(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private localeService: BsLocaleService) {
    // Define a localidade padrão como pt-BR
    this.localeService.use("pt-custom");
  }
}
